import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { supabase } from "../lib/supabase";
import { categorizeProduct } from "../lib/categorizeProduct";
import VariantForm, { ProductImage } from "./VariantForm";
import { buildThumbnailBlob } from "../utils/thumbnail";
import { recortarAlTope } from "../lib/limiteImagenesProducto";

/**
 * Botón "Agregar producto" + modal de alta (VariantForm), con la MISMA lógica
 * que la página de seleccionar productos: subida individual (con thumbnail
 * optimizado + webhook de categorización) y subida masiva (bulk, manejada
 * internamente por VariantForm). Reutilizable; al guardar llama a `onSaved`.
 */
export const AddProductFlow = ({
  companyId,
  brandId,
  onSaved,
  className,
}: {
  companyId: string | null;
  brandId: string | null;
  onSaved: () => Promise<void> | void;
  className?: string;
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProduct, setNewProduct] = useState<{
    nombre: string;
    images: ProductImage[];
  }>({ nombre: "", images: [] });
  const [savingProduct, setSavingProduct] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [rubro, setRubro] = useState<string>("Moda e Indumentaria");
  const [userPlan, setUserPlan] = useState<string>("gratis");

  // Rubro (de la marca activa) + plan (de la empresa) — para la UI/ gating del
  // VariantForm, igual que en seleccionar productos.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: company } = await supabase
          .from("company_profiles")
          .select("plan, rubro")
          .eq("id_company", companyId)
          .single();
        if (!cancelled && company?.plan) setUserPlan(company.plan as string);
        if (!cancelled && (company as any)?.rubro && !brandId)
          setRubro((company as any).rubro as string);
        if (brandId) {
          const { data: brand } = await supabase
            .from("brand_profiles")
            .select("rubro")
            .eq("id", brandId)
            .single();
          if (!cancelled && (brand as any)?.rubro)
            setRubro((brand as any).rubro as string);
        }
      } catch {
        /* noop — valores por defecto */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, brandId]);

  const handleSaveProduct = async () => {
    if (newProduct.images.length === 0) return;
    try {
      setSavingProduct(true);
      setImageError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      if (!companyId) throw new Error("No company associated with user");
      const idCompany = companyId;

      // Nombre placeholder si quedó vacío (el backend lo completa con IA).
      let effectiveName = newProduct.nombre.trim();
      if (!effectiveName) {
        const { count } = await supabase
          .from("productos")
          .select("id", { count: "exact", head: true })
          .eq("id_company", idCompany);
        effectiveName = `Producto ${(count ?? 0) + 1}`;
      }

      // 1) Crear producto (asociado a la marca activa si hay una)
      const productoPayload: Record<string, any> = {
        id_company: idCompany,
        nombre: effectiveName,
        tiene_variantes: false,
      };
      if (brandId) productoPayload.brand_id = brandId;
      const { data: producto, error: productoError } = await supabase
        .from("productos")
        .insert(productoPayload)
        .select("id")
        .single();
      if (productoError) throw productoError;
      const productId = String(producto.id);

      // 2) Crear variante
      const { data: variant, error: variantError } = await supabase
        .from("variantes")
        .insert({
          id_company: idCompany,
          nombre: effectiveName,
          activo: true,
          product_id: productId,
        })
        .select("id")
        .single();
      if (variantError) throw variantError;
      const variantId = variant.id as string;

      // 3) Subir imágenes (full + thumbnail opt) e insertar filas en DB
      // El picker (VariantForm) ya recorta al tope y avisa; este slice es la red
      // de contención del write path.
      const imagenesAlta = recortarAlTope(0, newProduct.images).aceptadas;
      for (let i = 0; i < imagenesAlta.length; i++) {
        const imgData = imagenesAlta[i];
        if (!imgData.tempFile) continue;

        const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
        if (imgData.tempFile.size > MAX_FILE_SIZE) {
          throw new Error(
            "El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.",
          );
        }

        const timestamp = Date.now();
        const originalName = imgData.fileName || imgData.tempFile.name;
        const sanitizedName = originalName.replace(/[^a-zA-Z0-9._-]/g, "_");
        const fileBaseName = `${timestamp}_${sanitizedName}`;

        const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
        const { error: fullErr } = await supabase.storage
          .from("productos")
          .upload(fullPath, imgData.tempFile);
        if (fullErr) throw fullErr;

        // Thumbnail optimizado vía canvas (best-effort).
        try {
          const blob = await buildThumbnailBlob(imgData.tempFile as File);
          if (!blob) throw new Error("no se pudo generar el thumbnail");
          const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
          const { error: optErr } = await supabase.storage
            .from("productos")
            .upload(optPath, blob);
          if (optErr)
            console.warn("Optimized upload failed, continuing", optErr);
        } catch (optErr) {
          console.warn("Error creating optimized image, continuing", optErr);
        }

        const { error: imgInsertError } = await supabase
          .from("productos_imagenes")
          .insert({
            id_company: idCompany,
            variante_id: variantId,
            url: fileBaseName,
            principal: imgData.principal || i === 0,
            orden: imgData.orden || i + 1,
          });
        if (imgInsertError) throw imgInsertError;
      }

      // Categorización IA (best-effort, fire-and-forget)
      categorizeProduct(String(productId), {
        modo: "crear",
        completarNombre: !newProduct.nombre.trim(),
        userId: user.id,
      });

      setShowAddForm(false);
      setNewProduct({ nombre: "", images: [] });
      await onSaved();
    } catch (err) {
      console.error("Error saving product:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al guardar producto",
      );
    } finally {
      setSavingProduct(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowAddForm(true)}
        className={`inline-flex items-center justify-center gap-2 h-[var(--btn-h)] px-5 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors ${className || ""}`}
      >
        <Plus className="w-4 h-4" />
        Agregar producto
      </button>

      {showAddForm && (
        <VariantForm
          editingVariant={null}
          newProduct={newProduct}
          loading={savingProduct}
          uploadingImages={{}}
          imageError={imageError}
          onCancel={() => {
            setShowAddForm(false);
            setNewProduct({ nombre: "", images: [] });
            setImageError(null);
          }}
          onSave={handleSaveProduct}
          onImageUpload={() => {}}
          onDeleteImage={() => {}}
          onSetPrincipalImage={() => {}}
          onReorderImages={() => {}}
          setEditingVariant={() => {}}
          setNewProduct={setNewProduct}
          rubro={rubro}
          idCompany={companyId}
          userPlan={userPlan}
          onBulkSuccess={async () => {
            await onSaved();
          }}
          brandId={brandId}
        />
      )}
    </>
  );
};
